import EntityExampleWrapper from 'client/components/core/EntityExampleWrapper'
import { slugs } from 'common/constants/inputTypes'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import React, { useEffect } from 'react'
import { contactFieldsQuery } from 'src/editor/api/queries/contact-fields-query'
import { useCalculateFormFieldStyles } from 'src/editor/shared/hooks/use-calculate-form-fields-styles'
import { getContactFieldTitle } from 'src/editor/shared/utils/get-contact-field-title'
import { Field } from 'src/shared/components/field'
import { FieldIconNew } from 'src/shared/components/field-icon-new'
import { FieldWrapper } from 'src/shared/components/field-wrapper'
import { PhoneNumberFieldNew } from 'src/shared/components/phone-number-field-new'
import { SelectNew } from 'src/shared/components/select-new'
import { ContactUsFieldEntityInterface } from 'src/shared/entities/contact-us-field/contact-us-field-entity.schema'
import { FieldEntityInterface } from 'src/shared/entities/field/field-entity.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CommonFieldSubEntityInterface } from 'src/shared/schemas/field/common-field-sub-entity.schema'

interface CommonEditorFieldEntityOrSubEntityProps {
  entityOrSubEntity:
    | FieldEntityInterface
    | ContactUsFieldEntityInterface
    | CommonFieldSubEntityInterface
}

export const CommonEditorFieldEntityOrSubEntity = ({
  entityOrSubEntity,
}: CommonEditorFieldEntityOrSubEntityProps) => {
  const { t } = useTypedTranslation()
  const { data: contactFields } = contactFieldsQuery.useQuery()
  // NOTE: `inputTypeName` is the final, already-translated string rendered by
  // `EntityExampleWrapper` with `skipTranslation`. Translating here once
  // avoids the double translation `<Trans>{t(...)}</Trans>` that returns ''
  // on prod, where `parseMissingKeyHandler: () => ''` blanks unknown keys.
  const [inputTypeName, setInputTypeName] = React.useState(
    t('entities.field.warnings.no_input_name'),
  )

  useEffect(() => {
    if (
      entityOrSubEntity.slug === slugs.PASSWORD ||
      entityOrSubEntity.slug === slugs.CONFIRM_PASSWORD
    ) {
      setInputTypeName('PASSWORD')
      return
    }

    if (entityOrSubEntity.slug === FieldSlugEnum.TwoFactorAuthCode) {
      setInputTypeName(t('website_settings.login.slug.code'))
      return
    }

    if (entityOrSubEntity.slug && contactFields?.length) {
      const field = contactFields.find(
        ({ slug }) => slug === entityOrSubEntity.slug,
      )
      if (field) {
        setInputTypeName(getContactFieldTitle(field, t))
      }
    } else {
      setInputTypeName(t('entities.field.warnings.no_input_name'))
    }
  }, [t, entityOrSubEntity.slug, contactFields])

  const iconColor = entityOrSubEntity.color || 'initial'
  const mobileIconColor = entityOrSubEntity.mobileColor || iconColor

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedFontFamily,
    calculatedColor,
    calculatedBackgroundColor,
  } = useCalculateFormFieldStyles({
    border: entityOrSubEntity.border,
    mobileBorder: entityOrSubEntity.mobileBorder,
    padding: entityOrSubEntity.padding,
    mobilePadding: entityOrSubEntity.mobilePadding,
    fontSize: entityOrSubEntity.fontSize,
    mobileFontSize: entityOrSubEntity.mobileFontSize,
    lineHeight: entityOrSubEntity.lineHeight,
    mobileLineHeight: entityOrSubEntity.mobileLineHeight,
    textAlign: entityOrSubEntity.textAlign,
    mobileTextAlign: entityOrSubEntity.mobileTextAlign,
    color: entityOrSubEntity.color,
    mobileColor: entityOrSubEntity.mobileColor,
    backgroundColor: entityOrSubEntity.backgroundColor,
    mobileBackgroundColor: entityOrSubEntity.mobileBackgroundColor,
  })

  if (entityOrSubEntity.slug === slugs.COUNTRY) {
    return (
      <SelectNew
        id={entityOrSubEntity.htmlAttrId}
        placeholderColor={entityOrSubEntity.placeholderColor}
        mobilePlaceholderColor={entityOrSubEntity.mobilePlaceholderColor}
        fontFamily={calculatedFontFamily.desktop}
        mobileFontFamily={calculatedFontFamily.mobile}
        textAlign={calculatedTextAlign.desktop}
        mobileTextAlign={calculatedTextAlign.mobile}
        lineHeight={calculatedLineHeight.desktop}
        mobileLineHeight={calculatedLineHeight.mobile}
        padding={calculatedPadding.desktop}
        mobilePadding={calculatedPadding.mobile}
        border={calculatedBorder.desktop}
        mobileBorder={calculatedBorder.mobile}
        backgroundColor={calculatedBackgroundColor.desktop}
        mobileBackgroundColor={calculatedBackgroundColor.mobile}
        color={calculatedColor.desktop}
        mobileColor={calculatedColor.mobile}
        fontSize={calculatedFontSize.desktop}
        mobileFontSize={calculatedFontSize.mobile}
        boxShadow={entityOrSubEntity.boxShadow}
        mobileBoxShadow={entityOrSubEntity.mobileBoxShadow}
      >
        <option>{t('entities.field.country.placeholder')}</option>
      </SelectNew>
    )
  }

  return (
    <EntityExampleWrapper
      label={inputTypeName}
      warning={Boolean(inputTypeName) === false}
      skipTranslation
    >
      <FieldWrapper>
        {entityOrSubEntity.iconClassName && (
          <FieldIconNew
            color={iconColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            mobileColor={mobileIconColor}
            className={entityOrSubEntity.iconClassName}
          />
        )}
        {entityOrSubEntity.slug === slugs.PHONE_NUMBER ? (
          <PhoneNumberFieldNew
            fontFamily={calculatedFontFamily.desktop}
            mobileFontFamily={calculatedFontFamily.mobile}
            hasIcon={!!entityOrSubEntity.iconClassName}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            padding={calculatedPadding.desktop}
            mobilePadding={calculatedPadding.mobile}
            border={calculatedBorder.desktop}
            mobileBorder={calculatedBorder.mobile}
            backgroundColor={calculatedBackgroundColor.desktop}
            mobileBackgroundColor={calculatedBackgroundColor.mobile}
            color={calculatedColor.desktop}
            mobileColor={calculatedColor.mobile}
            placeholder={entityOrSubEntity.placeholder}
            placeholderColor={entityOrSubEntity.placeholderColor}
            mobilePlaceholderColor={entityOrSubEntity.mobilePlaceholderColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            boxShadow={entityOrSubEntity.boxShadow}
            mobileBoxShadow={entityOrSubEntity.mobileBoxShadow}
            inputProps={{ readOnly: true }}
            countrySelectorProps={{
              buttonProps: { onClick: undefined },
              buttonStyle: { cursor: 'default' },
            }}
            wrapperProps={{
              id: entityOrSubEntity.htmlAttrId,
            }}
          />
        ) : (
          <Field
            fontFamily={calculatedFontFamily.desktop}
            mobileFontFamily={calculatedFontFamily.mobile}
            placeholder={entityOrSubEntity.placeholder}
            padding={calculatedPadding.desktop}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            mobilePadding={calculatedPadding.mobile}
            id={entityOrSubEntity.htmlAttrId}
            border={calculatedBorder.desktop}
            mobileBorder={calculatedBorder.mobile}
            backgroundColor={calculatedBackgroundColor.desktop}
            mobileBackgroundColor={calculatedBackgroundColor.mobile}
            color={calculatedColor.desktop}
            mobileColor={calculatedColor.mobile}
            placeholderColor={entityOrSubEntity.placeholderColor}
            mobilePlaceholderColor={entityOrSubEntity.mobilePlaceholderColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            boxShadow={entityOrSubEntity.boxShadow}
            mobileBoxShadow={entityOrSubEntity.mobileBoxShadow}
          />
        )}
      </FieldWrapper>
    </EntityExampleWrapper>
  )
}
